import React, { useEffect, useState } from "react";
import api from "../lib/api";

/**
 * Renders admin-defined custom field inputs for a given entity.
 * Props:
 *  - entity: "employee" | "report" | "transaction"
 *  - values: object { [key]: value }
 *  - onChange: (nextValues) => void
 */
export default function CustomFieldsSection({ entity, values, onChange }) {
  const [defs, setDefs] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get("/custom-fields", { params: { entity } })
      .then(({ data }) => { if (alive) setDefs(data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [entity]);

  if (!defs.length) return null;

  const setField = (key, v) => onChange({ ...(values || {}), [key]: v });

  return (
    <div className="md:col-span-2 border-t border-[#E5E7EB] pt-4 mt-2" data-testid={`custom-fields-${entity}`}>
      <div className="label-caps mb-3">Custom fields</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {defs.map((d) => (
          <div key={d.id}>
            <label className="label-caps block mb-1.5">
              {d.label}{d.required && <span className="text-[#DC2626]"> *</span>}
            </label>
            {d.type === "select" ? (
              <select required={d.required} value={(values || {})[d.key] || ""}
                      onChange={(e) => setField(d.key, e.target.value)}
                      data-testid={`cf-${entity}-${d.key}`}
                      className="w-full border border-[#E5E7EB] px-3 py-2 text-sm">
                <option value="">Select…</option>
                {(d.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input
                type={d.type === "number" ? "number" : d.type === "date" ? "date" : "text"}
                required={d.required}
                value={(values || {})[d.key] || ""}
                onChange={(e) => setField(d.key, e.target.value)}
                data-testid={`cf-${entity}-${d.key}`}
                className="w-full border border-[#E5E7EB] px-3 py-2 text-sm"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Read-only display of custom-field values in list/view screens. */
export function CustomFieldsView({ entity, values }) {
  const [defs, setDefs] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get("/custom-fields", { params: { entity } })
      .then(({ data }) => { if (alive) setDefs(data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [entity]);

  const filled = defs.filter((d) => values && values[d.key] !== undefined && values[d.key] !== "");
  if (!filled.length) return null;

  return (
    <div className="grid grid-cols-2 gap-3 mt-3">
      {filled.map((d) => (
        <div key={d.id} className="border border-[#E5E7EB] px-3 py-2">
          <div className="label-caps text-[10px]">{d.label}</div>
          <div className="text-sm text-[#111827] mt-0.5">{String(values[d.key])}</div>
        </div>
      ))}
    </div>
  );
}
